import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getBlogPosts,
  getBlogCategories,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  bulkDeleteBlogPosts,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
} from "@/lib/api";
import { CategoryManager } from "@/components/shared/CategoryManager";
import { StateImagesPanel } from "@/pages/admin/AdminStateImages";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  PenSquare,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Eye,
  Calendar,
  Tag,
  FolderPlus,
  Search,
  X,
  CheckSquare,
  Square,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code,
  Link2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import ImageExtension from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";

function RichTextEditor({ content, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
          class: "text-teal underline underline-offset-2 hover:text-teal-dark",
        },
      }),
      ImageExtension,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const ToolBtn = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${active ? "bg-teal/20 text-teal" : "text-slate-500 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const input = window.prompt("Enter URL (leave empty to remove link):", prev);
    if (input === null) return; // cancelled
    const raw = input.trim();

    // Empty -> unset link
    if (raw === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // Normalize URL — add https:// if no protocol (except mailto:/tel:/#anchors)
    let url = raw;
    if (!/^(https?:|mailto:|tel:|\/|#)/i.test(url)) {
      url = "https://" + url;
    }

    // Validate URL shape
    try {
      // URL() accepts mailto:, tel:, and http(s): as absolute; relative paths starting with / or # are fine
      if (!/^(\/|#)/.test(url)) new URL(url);
    } catch (e) {
      toast.error("Please enter a valid URL");
      return;
    }

    const { from, to, empty } = editor.state.selection;
    if (empty) {
      // No selection — insert the URL as text and link it
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: raw,
          marks: [{ type: "link", attrs: { href: url, target: "_blank", rel: "noopener noreferrer" } }],
        })
        .run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url, target: "_blank", rel: "noopener noreferrer" })
        .setTextSelection({ from, to })
        .run();
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap gap-0.5 p-2 border-b border-slate-200 bg-slate-50">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><UnderlineIcon className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><Strikethrough className="w-4 h-4" /></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1 self-center" />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="H1"><span className="text-xs font-bold">H1</span></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="H2"><span className="text-xs font-bold">H2</span></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="H3"><span className="text-xs font-bold">H3</span></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1 self-center" />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet List"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Ordered List"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote"><Quote className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code Block"><Code className="w-4 h-4" /></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1 self-center" />
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align Left"><AlignLeft className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align Center"><AlignCenter className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align Right"><AlignRight className="w-4 h-4" /></ToolBtn>
        <div className="w-px h-6 bg-slate-200 mx-1 self-center" />
        <ToolBtn onClick={addLink} active={editor.isActive("link")} title="Add Link"><Link2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo className="w-4 h-4" /></ToolBtn>
      </div>
      <EditorContent editor={editor} className="prose prose-sm max-w-none p-4 min-h-[200px] focus-within:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[200px]" />
    </div>
  );
}

const emptyPost = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  featured_image: "",
  category: "",
  tags: [],
  author: "",
  status: "draft",
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [isCategoryDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState(emptyPost);
  const [tagsInput, setTagsInput] = useState("");
  const [activeTab, setActiveTab] = useState("posts");
  // Filters / bulk selection
  const [searchQ, setSearchQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchData = async () => {
    try {
      const [postsData, categoriesData] = await Promise.all([
        getBlogPosts("all"),
        getBlogCategories(),
      ]);
      setPosts(postsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching blog data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenPostDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData(post);
      setTagsInput((post.tags || []).join(", "));
    } else {
      setEditingPost(null);
      setFormData(emptyPost);
      setTagsInput("");
    }
    setIsPostDialogOpen(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingPost ? formData.slug : generateSlug(title),
    });
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const postData = {
      ...formData,
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
    };

    try {
      if (editingPost) {
        await updateBlogPost(editingPost.id, postData);
        toast.success("Post updated successfully");
      } else {
        await createBlogPost(postData);
        toast.success("Post created successfully");
      }
      setIsPostDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await deleteBlogPost(postId);
      toast.success("Post deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  // Filter posts by search, category, status
  const filteredPosts = posts.filter((p) => {
    if (filterCategory !== "all" && p.category !== filterCategory) return false;
    if (filterStatus !== "all" && p.status !== filterStatus) return false;
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      const hay = `${p.title || ""} ${p.excerpt || ""} ${(p.tags || []).join(" ")} ${p.slug || ""} ${p.category || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const allSelected = filteredPosts.length > 0 && filteredPosts.every((p) => selectedIds.has(p.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPosts.map((p) => p.id)));
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} blog post${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteBlogPosts(ids);
      toast.success(`Deleted ${res.deleted} of ${res.requested} posts`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  return (
    <AdminLayout title="Blog Management" breadcrumb="Blog">
      <div className="space-y-6" data-testid="admin-blog-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="posts">Posts</TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
              <TabsTrigger value="state-images" data-testid="tab-state-images">State Images</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              {activeTab === "posts" && (
                <Button
                  onClick={() => handleOpenPostDialog()}
                  className="bg-teal hover:bg-teal-dark text-white"
                  data-testid="create-post-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              )}
            </div>
          </div>

          {/* Posts Tab */}
          <TabsContent value="posts" className="mt-6">
            {/* Search + Filter + Bulk toolbar */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search posts by title, excerpt, tag, slug…"
                  className="pl-9 pr-9"
                  data-testid="blog-search-input"
                />
                {searchQ && (
                  <button
                    type="button"
                    onClick={() => setSearchQ("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="blog-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="min-w-[180px]">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger data-testid="blog-filter-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[140px]">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="blog-filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedIds.size > 0 && (
                <Button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  variant="destructive"
                  data-testid="blog-bulk-delete-btn"
                >
                  {bulkDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete {selectedIds.size} selected
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-3 font-body" data-testid="blog-count">
              Showing {filteredPosts.length} of {posts.length} posts
              {(searchQ || filterCategory !== "all" || filterStatus !== "all") && " (filtered)"}
            </p>
            {loading ? (
              <p className="text-slate-500 text-center py-8 font-body">Loading posts...</p>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <PenSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-body">{posts.length === 0 ? "No blog posts yet" : "No posts match your filters"}</p>
                <p className="text-slate-400 text-sm font-body">{posts.length === 0 ? "Create your first post to get started" : "Try clearing search or filters"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 px-1">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-navy font-body"
                    data-testid="blog-select-all"
                  >
                    {allSelected ? <CheckSquare className="w-4 h-4 text-teal" /> : <Square className="w-4 h-4" />}
                    <span>{allSelected ? "Deselect all" : "Select all"}</span>
                  </button>
                </div>
                {filteredPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.02, 0.5) }}
                  >
                    <Card className={`border-slate-200 ${selectedIds.has(post.id) ? "ring-2 ring-teal" : ""}`} data-testid={`blog-post-row-${post.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <button
                            type="button"
                            onClick={() => toggleOne(post.id)}
                            className="flex-shrink-0 text-slate-400 hover:text-teal"
                            data-testid={`blog-select-${post.id}`}
                            aria-label="Toggle selection"
                          >
                            {selectedIds.has(post.id) ? <CheckSquare className="w-5 h-5 text-teal" /> : <Square className="w-5 h-5" />}
                          </button>
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {post.featured_image ? (
                              <img
                                src={post.featured_image}
                                alt=""
                                className="w-16 h-12 rounded-lg object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-16 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <PenSquare className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <h3 className="font-heading font-semibold text-navy text-sm truncate">
                                {post.title}
                              </h3>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    post.status === "published"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {post.status}
                                </span>
                                {post.category && (
                                  <span className="text-xs text-slate-500 font-body">{post.category}</span>
                                )}
                                <span className="text-xs text-slate-400 font-body flex items-center gap-1">
                                  <Eye className="w-3 h-3" /> {post.views || 0}
                                </span>
                                <span className="text-xs text-slate-400 font-body flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(post.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenPostDialog(post)}
                              data-testid={`edit-post-${post.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeletePost(post.id)}
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`delete-post-${post.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-6">
            <CategoryManager
              categories={categories}
              nameField="name"
              slugField="slug"
              onCreate={async (d) => { await createBlogCategory(d); fetchData(); }}
              onUpdate={async (id, d) => { await updateBlogCategory(id, d); fetchData(); }}
              onDelete={async (id) => { await deleteBlogCategory(id); fetchData(); }}
            />
          </TabsContent>

          {/* State Images Tab */}
          <TabsContent value="state-images" className="mt-6">
            <StateImagesPanel />
          </TabsContent>
        </Tabs>

        {/* Post Create/Edit Dialog */}
        <Dialog open={isPostDialogOpen} onOpenChange={setIsPostDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">
                {editingPost ? "Edit Post" : "Create Post"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitPost} className="space-y-4" data-testid="blog-post-form">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  required
                  placeholder="Post title"
                  data-testid="post-title-input"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  placeholder="post-url-slug"
                  data-testid="post-slug-input"
                />
              </div>
              <div>
                <Label htmlFor="excerpt">Excerpt *</Label>
                <Textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  required
                  rows={2}
                  placeholder="Brief summary of the post"
                  data-testid="post-excerpt-input"
                />
              </div>
              <div>
                <Label htmlFor="content">Content *</Label>
                <div className="mt-1" data-testid="post-content-input">
                  <RichTextEditor
                    content={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger data-testid="post-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.slug} value={cat.slug}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => setFormData({ ...formData, status: val })}
                  >
                    <SelectTrigger data-testid="post-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="featured_image">Featured Image URL</Label>
                <Input
                  id="featured_image"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  data-testid="post-image-input"
                />
              </div>
              <div>
                <Label htmlFor="author">Author</Label>
                <Input
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Author name"
                  data-testid="post-author-input"
                />
              </div>
              <div>
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="wireless, deals, news"
                  data-testid="post-tags-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-teal hover:bg-teal-dark text-white"
                data-testid="submit-post-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingPost ? "Updating..." : "Creating..."}
                  </>
                ) : editingPost ? (
                  "Update Post"
                ) : (
                  "Create Post"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

      </div>
    </AdminLayout>
  );
}
